import React, {useEffect, useState} from 'react';
import type {UIMatch} from 'react-router-dom';
import {Outlet, useMatches} from 'react-router-dom';
import styled from 'styled-components';
import {useMediaQuery} from '@mui/material';
import Sidebar from './Sidebar/Sidebar';
import {PageTitleKeys} from '../../config/routeConstants';
import {usePageTitle} from '../../hooks/usePageTitle';
import NavBar from "./Navbar";
import { usePageDescription } from '../../hooks/usePageDescription';
import ResizeObserverErrorBoundary from '../chat/ResizeObserverErrorBoundary';

interface RouteHandle {
    titleKey?: PageTitleKeys;
}

const LayoutWrapper = styled.div`
    display: flex;
    height: 100vh;
    flex-direction: column;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex: 1;
`;

const MainContent = styled.div<{ $sidebarOpen: boolean; $isMobile: boolean }>`
    flex: 1;
    margin-left: ${({$sidebarOpen, $isMobile}) => 
        $isMobile ? '0' : ($sidebarOpen ? '305px' : '60px')};
    transition: margin-left 0.3s;
    padding: 20px;
    overflow-y: auto;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 959px) {
        padding: 16px;
    }
    @media (max-width: 500px) {
        padding: 8px;
    }
            @media (max-width: 400px) {
        padding: 4px;
    }
`;

const Layout = () => {
    const isMobile = useMediaQuery('(max-width: 959px)');
    const [isSidebarOpen, setSidebarOpen] = useState(!isMobile);
    const matches = useMatches();

    const lastMatch = matches[matches.length - 1] as UIMatch<unknown, RouteHandle> | undefined;
    const titleKey = lastMatch?.handle?.titleKey || PageTitleKeys.Default;
    usePageTitle(titleKey);
    usePageDescription(titleKey);

    useEffect(() => {
        if (isMobile) {
            setSidebarOpen(false);
        } else {
            setSidebarOpen(true);
        }
    }, [isMobile]);

    const handleSidebarToggle = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    return (
        <ResizeObserverErrorBoundary>
            <LayoutWrapper>
                <NavBar/>
                <ContentWrapper>
                    <Sidebar isOpen={isSidebarOpen} onToggle={handleSidebarToggle}/>
                    <MainContent $sidebarOpen={isSidebarOpen} $isMobile={isMobile}>
                        <Outlet/>
                    </MainContent>
                </ContentWrapper>
            </LayoutWrapper>
        </ResizeObserverErrorBoundary>
    );
};

export default Layout;
